import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { isPresent } from '@luminovo/commons';
import {
    colorSystem,
    Divider,
    Flexbox,
    IconElement,
    InfoFilledIcon,
    MenuItem,
    TertiaryButton,
    Text,
    Tooltip,
} from '@luminovo/design-system';
import { ListItem, Menu, MenuList, Paper, Popper, styled, TableCell, TableRow } from '@mui/material';
import { useSelector } from '@tanstack/react-store';
import {
    ArrowDownIcon,
    ArrowUpDownIcon,
    ArrowUpIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    ListFilterIcon,
    XIcon,
} from 'lucide-react';
import React, { type JSX } from 'react';

import {
    FilterArrIncludesSome,
    FilterEnumEqualsAny,
    FilterIncludesString,
    FilterInDateRange,
    FilterInMonetaryValueRange,
    FilterInNumberRange,
    FilterInQuantityRange,
    FilterInRelativeDateRange,
} from './Filters';
import { isFilterValue } from './Filters/createFilterValueUpdater';
import { FilterFunctionMenuItem } from './TanStackMenuBar/FilterMenu';
import { flexRender } from './tanstackPrimitives';
import { Column, Header, RowData, Table } from './tanstackTypes';
import {
    AVAILABILITY_RENDER_OPTIONS,
    AvailabilityRenderOption,
    getFilterConfig,
    MONETARY_VALUE_RENDER_OPTIONS,
    QUANTITY_RENDER_OPTIONS,
    SupportedFilterFn,
} from './type';
import { getCommonPinningStyles } from './utils';

export function FixedHeaderContent<TData extends RowData>({ table }: { table: Table<TData> }): JSX.Element {
    // The header reads every slice: sort and filter icons, widths and the resize handle, the select-all and expand-all.
    useSelector(table.store);
    return (
        <>
            {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                        <TableHeaderCell key={header.id} header={header} table={table} />
                    ))}
                </TableRow>
            ))}
        </>
    );
}

const menuStyle = {
    cursor: 'pointer',
    userSelect: 'none',
    color: 'inherit',
} as const;

function isFirstLeafOfGroup<TData extends RowData>(column: Column<TData, unknown>, table: Table<TData>): boolean {
    const parent = column.parent;
    if (!parent) return false;
    const leafColumns = parent.getLeafColumns();
    if (leafColumns.length === 0 || leafColumns[0].id !== column.id) return false;
    const allVisibleLeaves = table.getVisibleLeafColumns();
    if (allVisibleLeaves.length > 0 && allVisibleLeaves[0].id === column.id) return false;
    return true;
}

function isGroupHeader<TData extends RowData>(header: Header<TData, unknown>): boolean {
    return header.column.columns.length > 0;
}

function TableHeaderCell<TData extends RowData>({
    header,
    table,
}: {
    header: Header<TData, unknown>;
    table: Table<TData>;
}): JSX.Element {
    const [anchorEl, setAnchorEl] = React.useState<Element | undefined>(undefined);
    const isGroup = isGroupHeader(header);
    const hasColumnMenu = !isGroup && (header.column.getCanSort() || header.column.getCanFilter());

    const handleOnClick = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (isPresent(anchorEl) || !hasColumnMenu) {
                return;
            }
            setAnchorEl(e.currentTarget);
        },
        [anchorEl, hasColumnMenu],
    );

    const justifyContentMap = {
        left: 'flex-start',
        center: 'center',
        right: 'flex-end',
    } as const;

    const isPinned = header.column.getIsPinned();
    const canResize = !isPinned && header.column.getCanResize();
    const isFirstInGroup = !isGroup && isFirstLeafOfGroup(header.column, table);
    const needsGroupBorder = isGroup && header.index > 0;

    return (
        <TableCell
            variant="head"
            colSpan={header.colSpan}
            style={{
                ...getCommonPinningStyles(header.column),
                backgroundColor: colorSystem.neutral[0],
                opacity: 1,
                borderBottom: `1px solid ${colorSystem.neutral[2]}`,
                ...(isFirstInGroup || needsGroupBorder ? { borderLeft: `1px solid ${colorSystem.neutral[3]}` } : {}),
                ...(isGroup ? { height: 32, padding: '0 8px' } : {}),
            }}
        >
            {header.isPlaceholder ? null : isGroup ? (
                <Flexbox
                    style={{ padding: '2px 0' }}
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                    }}
                >
                    <Text variant={'inherit'} style={{ fontWeight: 600 }} showEllipsis={true}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                    </Text>
                </Flexbox>
            ) : (
                <Flexbox
                    onClick={handleOnClick}
                    style={hasColumnMenu ? menuStyle : undefined}
                    sx={{
                        alignItems: 'center',
                        justifyContent: justifyContentMap[header.column.columnDef.meta?.align ?? 'left'],
                    }}
                >
                    <Text variant={'inherit'} showEllipsis={true}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                    </Text>
                    <DescriptionIcon column={header.column} />
                    <ActionIcon column={header.column} />
                    <ColumnMenu column={header.column} table={table} anchorEl={anchorEl} setAnchorEl={setAnchorEl} />
                </Flexbox>
            )}
            {canResize && (
                <ResizeHandle
                    isResizing={header.column.getIsResizing()}
                    onTouchStart={header.getResizeHandler()}
                    onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => {
                        header.getResizeHandler()(e);
                    }}
                />
            )}
        </TableCell>
    );
}

/** A wider invisible hit area around the always-visible bar, its `::after`. */
const ResizeHandle = styled('div', {
    shouldForwardProp: (prop) => prop !== 'isResizing',
})<{ isResizing: boolean }>(({ isResizing }) => ({
    position: 'absolute',
    right: 0,
    top: 0,
    height: '100%',
    width: '9px',
    cursor: 'col-resize',
    zIndex: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    touchAction: 'none',
    '&::after': {
        content: '""',
        width: '3px',
        height: '12px',
        borderRadius: '2px',
        backgroundColor: isResizing ? colorSystem.blue[5] : colorSystem.neutral[3],
        transition: 'background-color 0.2s ease',
    },
    '&:hover::after': {
        backgroundColor: colorSystem.blue[5],
    },
}));

export function ColumnMenu<TData extends RowData>({
    column,
    table,
    anchorEl,
    setAnchorEl,
}: {
    column: Column<TData, unknown>;
    table: Table<TData>;
    anchorEl: Element | undefined;
    setAnchorEl: (el: Element | undefined) => void;
}): JSX.Element {
    const sharedContext = table.options.meta?.sharedContext;

    const handleClose = () => {
        const filterValue = column.getFilterValue();
        if (isFilterValue(filterValue)) {
            if (Array.isArray(filterValue.value) && filterValue.value.length === 0) {
                column.setFilterValue(undefined);
            } else if (filterValue.value === '' && filterValue.filterFn === 'includesString') {
                column.setFilterValue(undefined);
            }
        }

        setAnchorEl(undefined);
    };

    const handleClearSort = () => {
        column.clearSorting();
        handleClose();
    };

    const handleSortAsc = () => {
        column.toggleSorting(false);
        handleClose();
    };

    const handleSortDesc = () => {
        column.toggleSorting(true);
        handleClose();
    };

    const dataType = column.columnDef.meta?.dataType;

    if ((!column.getCanFilter() && !column.getCanSort() && !column.getCanPin()) || !isPresent(dataType)) {
        return <></>;
    }

    const ascLabel = {
        text: t`Sort A to Z`,
        number: t`Sort smallest to largest`,
        enum: t`Sort A to Z`,
        array: t`Sort A to Z`,
        date: t`Sort oldest to newest`,
        monetaryValue: t`Sort smallest to largest`,
        quantity: t`Sort smallest to largest`,
        availability: t`Sort smallest to largest`,
        generic: t`Sort A to Z`,
    };

    const descLabel = {
        text: t`Sort Z to A`,
        number: t`Sort largest to smallest`,
        enum: t`Sort Z to A`,
        array: t`Sort Z to A`,
        date: t`Sort newest to oldest`,
        monetaryValue: t`Sort largest to smallest`,
        quantity: t`Sort largest to smallest`,
        availability: t`Sort largest to smallest`,
        generic: t`Sort Z to A`,
    };

    return (
        <Menu anchorEl={anchorEl} open={isPresent(anchorEl)} onClose={handleClose} keepMounted={false} variant="menu">
            <FilterIncludesStringMenuItem column={column} onClose={handleClose} />
            <FilterInNumberRangeMenuItem column={column} onClose={handleClose} />
            <FilterInMonetaryValueRangeMenuItem column={column} onClose={handleClose} />
            <FilterInQuantityRangeMenuItem column={column} onClose={handleClose} />
            <FilterInDateRangeMenuItem column={column} onClose={handleClose} />
            <FilterEnumEqualsAnyMenuItem column={column} sharedContext={sharedContext} />
            <FilterArrIncludesSomeMenuItem column={column} sharedContext={sharedContext} />

            {column.getCanSort() && (
                <MenuItem
                    label={ascLabel[dataType]}
                    onClick={handleSortAsc}
                    startIcon={<ArrowUpIcon />}
                    disabled={column.getIsSorted() === 'asc'}
                />
            )}

            {column.getCanSort() && (
                <MenuItem
                    label={descLabel[dataType]}
                    onClick={handleSortDesc}
                    startIcon={<ArrowDownIcon />}
                    disabled={column.getIsSorted() === 'desc'}
                />
            )}

            {column.getCanSort() && column.getIsSorted() && (
                <MenuItem
                    label={t`Clear sorting`}
                    variant={'destructive'}
                    startIcon={<XIcon />}
                    onClick={handleClearSort}
                    disabled={!Boolean(column.getIsSorted())}
                />
            )}

            <AdditionalColumnMenus column={column} table={table} onCloseParent={handleClose} />
            <RenderOptionsMenuItem column={column} table={table} />
        </Menu>
    );
}

const HOVER_CLOSE_DELAY_MS = 200;

function HoverSubMenu({
    triggerLabel,
    triggerIcon,
    badge,
    children,
}: {
    triggerLabel: string;
    triggerIcon: IconElement;
    badge?: JSX.Element;
    children: React.ReactNode;
}) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const isOpen = Boolean(anchorEl);

    const clearCloseTimeout = React.useCallback(() => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    }, []);

    const startCloseTimeout = React.useCallback(() => {
        clearCloseTimeout();
        closeTimeoutRef.current = setTimeout(() => setAnchorEl(null), HOVER_CLOSE_DELAY_MS);
    }, [clearCloseTimeout]);

    React.useEffect(() => {
        return () => clearCloseTimeout();
    }, [clearCloseTimeout]);

    return (
        <>
            <MenuItem
                label={triggerLabel}
                startIcon={triggerIcon}
                badge={badge}
                onMouseEnter={(e: React.MouseEvent<HTMLLIElement>) => {
                    clearCloseTimeout();
                    setAnchorEl(e.currentTarget);
                }}
                onMouseLeave={startCloseTimeout}
                onClick={(e: React.MouseEvent<HTMLLIElement>) => {
                    if (isOpen) {
                        setAnchorEl(null);
                    } else {
                        setAnchorEl(e.currentTarget);
                    }
                }}
            />
            <Popper
                open={isOpen}
                anchorEl={anchorEl}
                placement="right-start"
                style={{ zIndex: 1300 }}
                modifiers={[{ name: 'offset', options: { offset: [0, 2] } }]}
            >
                <Paper elevation={1} style={{ minWidth: 220 }}>
                    <FlyoutMenuList autoFocus={false} onMouseEnter={clearCloseTimeout} onMouseLeave={startCloseTimeout}>
                        {children}
                    </FlyoutMenuList>
                </Paper>
            </Popper>
        </>
    );
}

const FlyoutMenuList = styled(MenuList)({
    '& .MuiListItem-divider': {
        borderBottom: 'none',
    },
});

const CountBadge = styled('div')({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colorSystem.primary[6],
    color: colorSystem.neutral.white,
    fontSize: 10,
    fontWeight: 600,
    padding: '0 4px',
});

function HoverSubMenuBadge({ activeCount }: { activeCount: number }) {
    return (
        <Flexbox
            gap="4px"
            sx={{
                alignItems: 'center',
            }}
        >
            {activeCount > 0 && <CountBadge>{activeCount}</CountBadge>}
            <ChevronRightIcon size={16} color={colorSystem.neutral[5]} />
        </Flexbox>
    );
}

function AdditionalColumnMenus<TData extends RowData>({
    column,
    table,
    onCloseParent,
}: {
    column: Column<TData, unknown>;
    table: Table<TData>;
    onCloseParent: () => void;
}) {
    const additionalFilterColumnIds = column.columnDef.meta?.additionalFilterColumnIds;
    const sharedContext = table.options.meta?.sharedContext;

    if (!additionalFilterColumnIds || additionalFilterColumnIds.length === 0) {
        return null;
    }

    const additionalColumns = additionalFilterColumnIds
        .map((id) => table.getColumn(id))
        .filter((col): col is Column<TData, unknown> => isPresent(col));

    if (additionalColumns.length === 0) {
        return null;
    }

    const filterableColumns = additionalColumns.filter((col) => col.getCanFilter());
    const sortableColumns = additionalColumns.filter((col) => col.getCanSort());

    if (filterableColumns.length === 0 && sortableColumns.length === 0) {
        return null;
    }

    return (
        <>
            <Divider />
            {filterableColumns.length > 0 && (
                <AdditionalFiltersHoverMenu
                    columns={filterableColumns}
                    sharedContext={sharedContext}
                    onCloseParent={onCloseParent}
                />
            )}
            {sortableColumns.length > 0 && (
                <AdditionalSortersHoverMenu columns={sortableColumns} onCloseParent={onCloseParent} />
            )}
        </>
    );
}

function AdditionalFiltersHoverMenu<TData extends RowData>({
    columns,
    sharedContext,
    onCloseParent,
}: {
    columns: Column<TData, unknown>[];
    sharedContext: unknown;
    onCloseParent: () => void;
}) {
    const activeFilterCount = columns.filter((col) => col.getIsFiltered()).length;

    const handleClearAllFilters = () => {
        columns.forEach((col) => col.setFilterValue(undefined));
        onCloseParent();
    };

    const needsTitle = (dataType: string) => ['number', 'monetaryValue', 'date', 'quantity'].includes(dataType);

    return (
        <HoverSubMenu
            triggerLabel={t`More filters`}
            triggerIcon={<ListFilterIcon />}
            badge={<HoverSubMenuBadge activeCount={activeFilterCount} />}
        >
            {columns.map((additionalCol, idx) => {
                const dataType = additionalCol.columnDef.meta?.dataType ?? 'text';
                return (
                    <React.Fragment key={additionalCol.id}>
                        {idx > 0 && <Divider />}
                        {needsTitle(dataType) && (
                            <ListItem style={{ backgroundColor: colorSystem.neutral.white, paddingBottom: 4 }}>
                                <Text variant="body-small-semibold" color={colorSystem.neutral[7]}>
                                    {additionalCol.columnDef.meta?.label()}
                                </Text>
                            </ListItem>
                        )}
                        <FilterIncludesStringMenuItem column={additionalCol} onClose={onCloseParent} />
                        <FilterInNumberRangeMenuItem column={additionalCol} onClose={onCloseParent} />
                        <FilterInMonetaryValueRangeMenuItem column={additionalCol} onClose={onCloseParent} />
                        <FilterInQuantityRangeMenuItem column={additionalCol} onClose={onCloseParent} />
                        <FilterInDateRangeMenuItem column={additionalCol} onClose={onCloseParent} />
                        <FilterEnumEqualsAnyMenuItem column={additionalCol} sharedContext={sharedContext} />
                        <FilterArrIncludesSomeMenuItem column={additionalCol} sharedContext={sharedContext} />
                    </React.Fragment>
                );
            })}
            {activeFilterCount > 0 && (
                <>
                    <Divider />
                    <MenuItem
                        label={t`Clear all filters`}
                        variant="destructive"
                        startIcon={<XIcon />}
                        onClick={handleClearAllFilters}
                    />
                </>
            )}
        </HoverSubMenu>
    );
}

function AdditionalSortersHoverMenu<TData extends RowData>({
    columns,
    onCloseParent,
}: {
    columns: Column<TData, unknown>[];
    onCloseParent: () => void;
}) {
    const activeSortCount = columns.filter((col) => col.getIsSorted()).length;

    const ascLabel: Record<string, string> = {
        text: t`Sort A to Z`,
        number: t`Sort smallest to largest`,
        enum: t`Sort A to Z`,
        array: t`Sort A to Z`,
        date: t`Sort oldest to newest`,
        monetaryValue: t`Sort smallest to largest`,
        quantity: t`Sort smallest to largest`,
    };

    const descLabel: Record<string, string> = {
        text: t`Sort Z to A`,
        number: t`Sort largest to smallest`,
        enum: t`Sort Z to A`,
        array: t`Sort Z to A`,
        date: t`Sort newest to oldest`,
        monetaryValue: t`Sort largest to smallest`,
        quantity: t`Sort largest to smallest`,
    };

    const handleClearAllSorting = () => {
        columns.forEach((col) => col.clearSorting());
        onCloseParent();
    };

    return (
        <HoverSubMenu
            triggerLabel={t`More sort options`}
            triggerIcon={<ArrowUpDownIcon />}
            badge={<HoverSubMenuBadge activeCount={activeSortCount} />}
        >
            {columns.map((additionalCol, idx) => {
                const dataType = additionalCol.columnDef.meta?.dataType ?? 'text';
                const label = additionalCol.columnDef.meta?.label() ?? t`Unknown`;
                const ascText = ascLabel[dataType] ?? t`Sort ascending`;
                const descText = descLabel[dataType] ?? t`Sort descending`;

                return (
                    <React.Fragment key={additionalCol.id}>
                        {idx > 0 && <Divider />}
                        <ListItem style={{ backgroundColor: colorSystem.neutral.white, paddingBottom: 4 }}>
                            <Text variant="body-small-semibold" color={colorSystem.neutral[7]}>
                                {label}
                            </Text>
                        </ListItem>
                        <Tooltip title={`${label}: ${ascText}`} placement="right" disableMaxWidth>
                            <MenuItem
                                label={ascText}
                                onClick={() => {
                                    additionalCol.toggleSorting(false);
                                    onCloseParent();
                                }}
                                startIcon={<ArrowUpIcon />}
                                disabled={additionalCol.getIsSorted() === 'asc'}
                            />
                        </Tooltip>
                        <Tooltip title={`${label}: ${descText}`} placement="right" disableMaxWidth>
                            <MenuItem
                                label={descText}
                                onClick={() => {
                                    additionalCol.toggleSorting(true);
                                    onCloseParent();
                                }}
                                startIcon={<ArrowDownIcon />}
                                disabled={additionalCol.getIsSorted() === 'desc'}
                            />
                        </Tooltip>
                        {additionalCol.getIsSorted() && (
                            <MenuItem
                                label={t`Clear sorting`}
                                variant="destructive"
                                startIcon={<XIcon />}
                                onClick={() => {
                                    additionalCol.clearSorting();
                                    onCloseParent();
                                }}
                            />
                        )}
                    </React.Fragment>
                );
            })}
            {activeSortCount > 0 && (
                <>
                    <Divider />
                    <MenuItem
                        label={t`Clear all sorting`}
                        variant="destructive"
                        startIcon={<XIcon />}
                        onClick={handleClearAllSorting}
                    />
                </>
            )}
        </HoverSubMenu>
    );
}

function FilterIncludesStringMenuItem<TData extends RowData>({
    column,
    onClose,
}: {
    column: Column<TData, unknown>;
    onClose: () => void;
}) {
    const filterConfig = getFilterConfig(column, 'text');
    if (!isPresent(filterConfig)) {
        return null;
    }

    return (
        <ListItem
            style={{ backgroundColor: colorSystem.neutral.white }}
            divider={column.getCanSort()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <Flexbox
                gap={8}
                style={{ width: '100%' }}
                sx={{
                    flexDirection: 'column',
                }}
            >
                <FilterFunctionMenuItem column={column} />
                <FilterIncludesString column={column} onClose={onClose} />
            </Flexbox>
        </ListItem>
    );
}

function FilterInNumberRangeMenuItem<TData extends RowData>({
    column,
    onClose,
}: {
    column: Column<TData, unknown>;
    onClose: () => void;
}) {
    const filterConfig = getFilterConfig(column, 'number');
    if (!isPresent(filterConfig)) {
        return null;
    }

    return (
        <ListItem
            style={{ backgroundColor: colorSystem.neutral.white }}
            divider={column.getCanSort()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <FilterInNumberRange column={column} onClose={onClose} />
        </ListItem>
    );
}

function FilterInMonetaryValueRangeMenuItem<TData extends RowData>({
    column,
    onClose,
}: {
    column: Column<TData, unknown>;
    onClose: () => void;
}) {
    const filterConfig = getFilterConfig(column, 'monetaryValue');
    if (!isPresent(filterConfig)) {
        return null;
    }

    return (
        <ListItem
            style={{ backgroundColor: colorSystem.neutral.white }}
            divider={column.getCanSort()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <FilterInMonetaryValueRange column={column} onClose={onClose} />
        </ListItem>
    );
}

function FilterInQuantityRangeMenuItem<TData extends RowData>({
    column,
    onClose,
}: {
    column: Column<TData, unknown>;
    onClose: () => void;
}) {
    const filterConfig = getFilterConfig(column, 'quantity');
    if (!isPresent(filterConfig)) {
        return null;
    }

    return (
        <ListItem
            style={{ backgroundColor: colorSystem.neutral.white }}
            divider={column.getCanSort()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <FilterInQuantityRange column={column} onClose={onClose} />
        </ListItem>
    );
}

function FilterInDateRangeMenuItem<TData extends RowData>({
    column,
    onClose,
}: {
    column: Column<TData, unknown>;
    onClose: () => void;
}) {
    const filterConfig = getFilterConfig(column, 'date');
    if (!isPresent(filterConfig)) {
        return null;
    }

    const filterValue = column.getFilterValue();
    const activeFilterFn = isFilterValue(filterValue) ? filterValue.filterFn : SupportedFilterFn.inDateRange;

    return (
        <ListItem
            style={{ backgroundColor: colorSystem.neutral.white }}
            divider={column.getCanSort()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <Flexbox
                gap={8}
                style={{ width: '100%' }}
                sx={{
                    flexDirection: 'column',
                }}
            >
                <FilterFunctionMenuItem column={column} />
                {activeFilterFn === SupportedFilterFn.isInLastDays ||
                activeFilterFn === SupportedFilterFn.isOlderThanDays ? (
                    <FilterInRelativeDateRange column={column} onClose={onClose} />
                ) : (
                    <FilterInDateRange column={column} onClose={onClose} />
                )}
            </Flexbox>
        </ListItem>
    );
}

function FilterEnumEqualsAnyMenuItem<TData extends RowData, TSharedContext>({
    column,
    sharedContext,
}: {
    column: Column<TData, unknown>;
    sharedContext: TSharedContext;
}) {
    const filterConfig = getFilterConfig(column, 'enum');
    if (!isPresent(filterConfig)) {
        return null;
    }

    return (
        <ListItem
            style={{ backgroundColor: colorSystem.neutral.white }}
            divider={column.getCanSort()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <Flexbox
                gap={8}
                style={{ width: '100%' }}
                sx={{
                    flexDirection: 'column',
                }}
            >
                <FilterFunctionMenuItem column={column} />
                <FilterEnumEqualsAny column={column} sharedContext={sharedContext} />
            </Flexbox>
        </ListItem>
    );
}

function FilterArrIncludesSomeMenuItem<TData extends RowData, TSharedContext>({
    column,
    sharedContext,
}: {
    column: Column<TData, unknown>;
    sharedContext: TSharedContext;
}) {
    const filterConfig = getFilterConfig(column, 'array');
    if (!isPresent(filterConfig)) {
        return null;
    }

    return (
        <ListItem
            style={{ backgroundColor: colorSystem.neutral.white }}
            divider={column.getCanSort()}
            onKeyDown={(e) => e.stopPropagation()}
        >
            <Flexbox
                gap={8}
                style={{ width: '100%' }}
                sx={{
                    flexDirection: 'column',
                }}
            >
                <FilterFunctionMenuItem column={column} />
                <FilterArrIncludesSome column={column} sharedContext={sharedContext} />
            </Flexbox>
        </ListItem>
    );
}

function RenderOptionsMenuItem<TData extends RowData>({
    column,
    table,
}: {
    column: Column<TData, unknown>;
    table: Table<TData>;
}) {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const renderOptionConfig = column.columnDef.meta?.renderOptionConfig;
    const dataType = column.columnDef.meta?.dataType;

    if (!renderOptionConfig || renderOptionConfig.disabled) {
        return null;
    }

    const options =
        dataType === 'monetaryValue'
            ? MONETARY_VALUE_RENDER_OPTIONS
            : dataType === 'quantity'
              ? QUANTITY_RENDER_OPTIONS
              : dataType === 'availability'
                ? AVAILABILITY_RENDER_OPTIONS
                : [];

    if (options.length === 0) {
        return null;
    }

    // The availability format is app-wide, shared by every table and the lead time card; others are per column.
    const isAvailability = dataType === 'availability';
    const columnRenderOptions = table.options.meta?.columnRenderOptions ?? {};
    const setColumnRenderOption = table.options.meta?.setColumnRenderOption;
    const displayFormat = table.options.meta?.displayFormat;
    const setDisplayFormat = table.options.meta?.setDisplayFormat;

    const currentValue = isAvailability
        ? (displayFormat?.leadTime ?? renderOptionConfig.default)
        : (columnRenderOptions[column.id] ?? renderOptionConfig.default);
    const currentOption = options.find((opt) => opt.id === currentValue) ?? options[0];

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (optionId?: string) => {
        if (optionId) {
            if (isAvailability) {
                setDisplayFormat?.({ leadTime: optionId as AvailabilityRenderOption });
            } else {
                setColumnRenderOption?.(column.id, optionId);
            }
        }
        setAnchorEl(null);
    };

    return (
        <>
            <Divider />
            <ListItem style={{ backgroundColor: colorSystem.neutral.white, paddingTop: 8, paddingBottom: 8 }}>
                <Flexbox
                    gap={4}
                    sx={{
                        alignItems: 'center',
                    }}
                >
                    <Text variant="body-small" color={colorSystem.neutral[7]}>
                        <Trans>Display style</Trans>
                    </Text>
                    <StyledRenderOptionButton size="small" onClick={handleClick} endIcon={<ChevronDownIcon />}>
                        {currentOption.label()}
                    </StyledRenderOptionButton>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => handleClose()}>
                        {options.map((option) => (
                            <MenuItem key={option.id} onClick={() => handleClose(option.id)} label={option.label()} />
                        ))}
                    </Menu>
                </Flexbox>
            </ListItem>
        </>
    );
}

const StyledRenderOptionButton = styled(TertiaryButton)({
    height: '20px',
    padding: '2px 4px',
    '& .MuiButton-endIcon': {
        marginLeft: '2px',
        marginRight: '-2px',
    },
});

function DescriptionIcon<TData extends RowData>({ column }: { column: Column<TData, unknown> }) {
    if (column.columnDef.meta?.description === undefined) {
        return null;
    }
    const description = column.columnDef.meta.description();
    const descriptionTitle = typeof description === 'string' ? description : description.title();
    const descriptionVariant = typeof description === 'string' ? undefined : description.variant;

    return (
        <Tooltip title={descriptionTitle} variant={descriptionVariant} arrow>
            <HeaderIconBox>
                <InfoFilledIcon size={12} color={colorSystem.neutral[5]} />
            </HeaderIconBox>
        </Tooltip>
    );
}

function ActionIcon<TData extends RowData>({ column }: { column: Column<TData, unknown> }) {
    if (column.getIsFiltered()) {
        return (
            <ActionIconBadge isActive>
                <ListFilterIcon size={12} />
            </ActionIconBadge>
        );
    }

    if (column.getIsSorted() === 'asc') {
        return (
            <ActionIconBadge isActive>
                <ArrowUpIcon size={12} />
            </ActionIconBadge>
        );
    }
    if (column.getIsSorted() === 'desc') {
        return (
            <ActionIconBadge isActive>
                <ArrowDownIcon size={12} />
            </ActionIconBadge>
        );
    }

    if (column.getCanFilter()) {
        return (
            <ActionIconBadge isActive={false}>
                <ListFilterIcon size={12} />
            </ActionIconBadge>
        );
    }

    if (column.getCanSort()) {
        return (
            <ActionIconBadge isActive={false}>
                <ArrowUpDownIcon size={12} />
            </ActionIconBadge>
        );
    }

    return null;
}

/** A 12px header icon in a 16px box, 4px after the header text. */
const HeaderIconBox = styled('span')({
    display: 'inline-flex',
    flexShrink: 0,
    padding: '2px',
    marginLeft: '4px',
});

/** The circle behind a header's sort or filter icon; the header cell handles its click and pointer cursor. */
const ActionIconBadge = styled(HeaderIconBox, {
    shouldForwardProp: (prop) => prop !== 'isActive',
})<{ isActive: boolean }>(({ isActive }) => ({
    borderRadius: '50%',
    backgroundColor: isActive ? colorSystem.primary[2] : colorSystem.neutral[1],
    color: isActive ? colorSystem.primary[6] : 'inherit',
    '&:hover': {
        backgroundColor: isActive ? colorSystem.primary[3] : colorSystem.neutral[2],
        color: isActive ? colorSystem.primary[7] : 'inherit',
    },
}));
